import omit from 'lodash/omit'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'

const useCategorySortFilter = () => {
  const router = useRouter()
  const [openFilterModal, setOpenFilterModal] = useState(false)

  const query = router.query

  const filters = useMemo(() => {
    const property = (query.property || '') as string
    const filters = property.split('|')

    return filters
  }, [query.property])

  const isCustomSortActive = (customSortName: string) =>
    query[customSortName] === 'true'

  const isSortActive = (direction: string) =>
    query['sortBy'] === 'calMediaPrice' && query['sortType'] === direction

  const isFilterActive = (filterName: string, filterValue: string) => {
    const filterIndex = filters.findIndex(x => x.startsWith(filterName))

    if (filterIndex === -1) {
      return false
    }

    const filter = filters[filterIndex]
    const filterValues = filter.split(':')[1]?.split(',') || []

    return filterValues.includes(filterValue)
  }

  const handleChangeCustomSort = (customSortName: string) => {
    let newQuery = { ...query }
    if (!isCustomSortActive(customSortName)) {
      newQuery[customSortName] = 'true'
    } else {
      newQuery = omit(newQuery, [customSortName])
    }

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    )
  }

  const handleChangeSort = (direction: string) => {
    let newQuery = { ...query }
    if (!isSortActive(direction)) {
      newQuery.sortBy = 'calMediaPrice'
      newQuery.sortType = direction
    } else {
      newQuery = omit(newQuery, ['sortBy', 'sortType'])
    }

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    )
  }

  const handleChangeFilter = (filterName: string, filterValue: string) => {
    let newQuery = { ...query }
    let newFilters = [...filters]

    const filterIndex = newFilters.findIndex(x => x.startsWith(filterName))

    if (filterIndex === -1) {
      newFilters.push(`${filterName}:${filterValue}`)
    } else {
      const filter = newFilters[filterIndex]
      let filterValues = filter.split(':')[1]?.split(',') || []
      const filterValueIndex = filterValues.indexOf(filterValue)

      if (filterValueIndex === -1) {
        filterValues.push(filterValue)
      } else {
        filterValues = filterValues.filter(x => x !== filterValue)
      }

      filterValues = filterValues.filter(x => x.trim())

      if (filterValues.length) {
        newFilters[filterIndex] = `${filterName}:${filterValues.join(',')}`
      } else {
        newFilters = newFilters.filter((_, i) => i !== filterIndex)
      }
    }

    newFilters = newFilters.filter(x => x.trim())

    if (newFilters.length) {
      newQuery.property = newFilters.join('|')
    } else {
      newQuery = omit(newQuery, 'property')
    }

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    )
  }

  const applyFilters = (newFilters: string[]) => {
    let newQuery = { ...query }
    newFilters = newFilters.filter(x => x.trim())

    if (newFilters.length) {
      newQuery.property = newFilters.join('|')
    } else {
      newQuery = omit(newQuery, 'property')
    }

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    )
  }

  const numberOfFilters = useMemo(
    () =>
      filters
        .map(x => (x.split(':')[1]?.split(',') || []).filter(x => x).length)
        .reduce((sum, cur) => sum + cur, 0),
    [filters]
  )

  return {
    handleChangeFilter,
    handleChangeSort,
    handleChangeCustomSort,
    isCustomSortActive,
    isFilterActive,
    isSortActive,
    openFilterModal,
    setOpenFilterModal,
    filters,
    applyFilters,
    numberOfFilters,
  }
}

export default useCategorySortFilter
